import { DigisubsGetRequest, DigisubsPostRequest, ServerErrorResponse, ServerResponse } from "./http.models";

export class HttpService {
  private baseURL: string;
  private cacheDuration: number; // Cache duration in seconds

  /**
   * 
   * @param baseURL base URL for the API
   * @param cacheDuration cache duration in seconds
   */
  constructor(baseURL: string, cacheDuration: number = 5 * 60) {
    this.baseURL = baseURL;
    this.cacheDuration = cacheDuration * 1000;
    //console.log("cacheDuration", this.cacheDuration);
  }

  async get<T>(endpoint: string, cachekey?: string, apiKey?: string, init?: Pick<RequestInit, 'credentials'>): Promise<T> {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const key = (cachekey) ? cachekey : fullUrl;
    const cachedData = this.getFromCache(key);

    if (cachedData) {
      return cachedData as T;
    }
    const myHeaders = new Headers();
    if (apiKey) {
      myHeaders.append("X-Api-Key", apiKey);
    }
    const response = await fetch(fullUrl, {
      headers: myHeaders,
      method: 'GET',
      ...init,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    this.saveToCache(key, data);

    return data;
  }

  entitlerPost: DigisubsPostRequest = async (endpoint: string, body: Record<string, any>, apiKey: string): Promise<string> => {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("X-Api-Key", apiKey);

    const raw = JSON.stringify(body);

    const requestOptions: RequestInit = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };
    try {
      const response = await fetch(fullUrl, requestOptions);
      if (!response.ok) {
        //console.log("Not Ok");
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.jwt;
    } catch (error) {
      //console.log("catch", error);
      throw error;
      //return null;
    }

  }

  async entitlerPost2<T>(endpoint: string, body: any, apiKey: string, method: string = 'POST'): Promise<ServerResponse<T>> {
    try {

      const fullUrl = `${this.baseURL}${endpoint}`;

      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("X-Api-Key", apiKey);

      const res = await fetch(fullUrl, {
        method,
        headers: myHeaders,
        body: JSON.stringify(body),
        redirect: "follow"
      });
      //const payload: ServerResponse<T> = await res.json();
      const data = await res.json();
      if (!res.ok) {
        // assume payload is already your Auth0ServerErrorResponse
        return {
          success: false, error: {
            error: "EntitlerError",
            description: res.statusText,
            message: "There was a server issue. Please try again later.",
            code: "ENT_SRV_ERR"
          }
        };
      }
      
      let fixedData;
      if (data?.data?.body) {
        fixedData = data.data.body;
      } else if (data?.data) {
        fixedData = data.data;
      } else {
        fixedData = data;
      }
      //return payload;
      return { success: true, data: fixedData as T };
    } catch (err: any) {
      // here’s the generic error‐mapper:
      return {
        success: false,
        error: {
          error: err.name ?? "FetchError",
          description: err.message ?? String(err),
          message: "There was a server issue. Please try again later.",
          code: "SRV_ERR"
        },
      };
    }
  }

  private parseBodyAsJsonOrText<T>(body: string): T {
    const trimmed = body.trim();
    if (!trimmed) {
      return {} as T;
    }
    try {
      return JSON.parse(trimmed) as T;
    } catch {
      return trimmed as unknown as T;
    }
  }

  async post<T>(endpoint: string, body, apiKey?: string, init?: Pick<RequestInit, 'credentials' | 'headers'>): Promise<T> {
    const fullUrl = `${this.baseURL}${endpoint}`;

    const myHeaders = new Headers();
    //myHeaders.append("Content-Type", "application/json");
    if (apiKey) {
      myHeaders.append("X-Api-Key", apiKey);
    }
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: myHeaders,
        mode: 'cors',
        body: JSON.stringify(body),
        ...init,
      });

      const text = await response.text();

      if (!response.ok) {
        const hint = text ? `: ${text.slice(0, 200)}` : '';
        throw new Error(`HTTP error! status: ${response.status}${hint}`);
      }

      return this.parseBodyAsJsonOrText<T>(text);
    } catch (error) {
      throw error;
    }


  }

  // testing using this. Main difference is it will always return a ServerResponse so that I don't need to have a catch in my api calls. I just need to check success flag.
  async post2<T>(endpoint: string, body, apiKey?: string): Promise<ServerResponse<T>> {
    try {

      const fullUrl = `${this.baseURL}${endpoint}`;

      const myHeaders = new Headers();

      if (apiKey) {
        myHeaders.append("X-Api-Key", apiKey);
      }

      const res = await fetch(fullUrl, {
        method: 'POST',
        headers: myHeaders,
        mode: 'cors',
        body: JSON.stringify(body)
      });
      const payload: ServerResponse<T> = await res.json();

      if (!res.ok) {
        // assume payload is already your Auth0ServerErrorResponse
        return { success: false, error: payload.error };
      }
      if(payload.success !== false) payload.success = true;
      return { success: true, data: payload.data as T };
      //return payload;
      //return { success: true, data: payload as T };
    } catch (err: any) {
      // here’s the generic error‐mapper:
      return {
        success: false,
        error: {
          error: err.name ?? "FetchError",
          description: err.message ?? String(err),
          message: "There was a server issue. Please try again later.",
          code: "SRV_ERR"
        },
      };
    }
  }

  private getFromCache<T>(url: string): T | null {
    const cachedEntry = localStorage.getItem(url);
    if (!cachedEntry) {
      return null;
    }

    try {
      const { data, timestamp } = JSON.parse(cachedEntry);
      const isExpired = Date.now() - timestamp > this.cacheDuration;

      if (isExpired) {
        localStorage.removeItem(url); // Remove expired cache
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error parsing cache data:", error);
      localStorage.removeItem(url); // Clear corrupt cache
      return null;
    }
  }

  private saveToCache(url: string, data: unknown): void {
    const ts = Date.now() + this.cacheDuration;
    const expirationString = new Date(ts).toLocaleString();
    const cacheEntry = {
      data,
      timestamp: ts,
      expirationString
    };

    localStorage.setItem(url, JSON.stringify(cacheEntry));
  }

  clearCache(url?: string): void {
    if (url) {
      localStorage.removeItem(url);
    } else {
      localStorage.clear();
    }
  }

  async delete<T>(endpoint: string, apiKey?: string, init?: Pick<RequestInit, 'credentials' | 'keepalive'>): Promise<T> {
    const fullUrl = `${this.baseURL}${endpoint}`;
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
  
    if (apiKey) {
      myHeaders.append("X-Api-Key", apiKey);
    }

    try {
      const response = await fetch(fullUrl, {
        method: 'DELETE',
        headers: myHeaders,
        ...init,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return {} as T;
      }

      const text = await response.text();
      return this.parseBodyAsJsonOrText<T>(text);
    } catch (error) {
      throw error;
    }
  }
}


